import CarManagmentProvider from 'providers/CarManagmentProvider';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
require('dayjs/locale/pt');
dayjs.extend(localeData);
dayjs.extend(LocalizedFormat);
dayjs.extend(weekday);
// import 'flatpickr/dist/themes/material_blue.css';
import BasicGanttChart, {
  Trip
} from 'components/charts/dhtmlx/BasicGanttChart_Alocation';
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  useCarManagmentContext,
  Trip as TripStructure
} from 'providers/CarManagmentProvider';
import { Modal, Spinner } from 'react-bootstrap';
// import { useBreakpoints } from 'providers/BreakpointsProvider';
import Button from 'components/base/Button';

/**
 * Formats the request's start or end timestamp for display in Portuguese.
 * @param date Timestamp to render in the request details.
 */
const formatDateTime = (date: Date) => {
  return date.toLocaleString('pt-PT', {
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Mounts the scheduling page beneath the car-management provider it consumes.
 */
const index = () => {
  return (
    <CarManagmentProvider>
      <VehicleSchedulingPage />
    </CarManagmentProvider>
  );
};

// Describes the formatted trip/request fields used by the scheduling page and Gantt chart.
interface TripRequest {
  id: number;
  user: number;
  companiesList: string;
  mainPassenger: string;
  organizer: string;
  listPassenger: string;
  outsideUsers: number;
  grupo: number;
  start_date: Date;
  end_date: Date;
  originalStart: Date;
  originalEnd: Date;
  duration: number;
  takecar: string;
  leavecar: string;
  isClipped: boolean;
  isConfirmed: boolean;
  maxPassengers?: number;
  statusToSet: string;
  status: string;
}

// Identifies the vehicle currently selected for a request.
interface ActualVehicle {
  id: number | null;
  carPlate: string | null;
}

// Stores one unsaved status and vehicle assignment for a trip request.
interface PendingManagment {
  requestId: number;
  carID: number | null;
  carPlate: string | null;
  grupo: number;
  typeOfAlocation: string;
}

/**
 * Coordinates the allocation chart, vehicle availability, and staged trip updates.
 * Provider trips are formatted for the Gantt view while proposed edits remain local
 * until the user confirms them.
 */
const VehicleSchedulingPage = () => {
  const {
    // Persisted trip records used to construct the chart's display data.
    trips,
    // Vehicle identities and capacities available for assignment.
    carsTrips,
    // Maintenance/observation events overlapping the currently selected window.
    CarEventsOverlap,
    // Provider mutation used to persist staged trip decisions.
    bulkUpdateTrips,
    // Fetches maintenance overlaps when a request is opened.
    fetchCarsMaintenanceOverlap
  } = useCarManagmentContext();
  // const { breakpoints } = useBreakpoints();

  // Local proposed decisions that have not yet been saved through the provider.
  const [pendingManagments, setPendingManagments] = useState<
    PendingManagment[]
  >([]);
  // ID of the request currently open in the details modal.
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(
    null
  );
  // Vehicle chosen for the selected request before staging its decision.
  const [selectedVehicle, setSelectedVehicle] = useState<ActualVehicle | null>(
    null
  );
  // Controls whether the selected request's details modal is shown.
  const [showModal, setShowModal] = useState(false);
  // Current reasons that prevent approving the selected request.
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // Trip IDs currently visible in the Gantt chart viewport.
  const [visibleTripIds, setVisibleTripIds] = useState<number[]>([]);
  // Trip IDs in the complete Monday-to-Friday week shown by the Gantt chart.
  const [visibleWeeklyTripIds, setVisibleWeeklyTripIds] = useState<number[]>(
    []
  );
  // Prevents duplicate submissions and communicates an in-progress save.
  const [isConfirming, setIsConfirming] = useState(false);

  /**
   * Closes the modal and resets transient state belonging to its selection.
   */
  const cleanData = () => {
    setShowModal(false);
    setSelectedRequestId(null);
    setSelectedVehicle(null);
    setValidationErrors([]);
  };

  /**
   * Converts a provider trip into the display and allocation shape used by the chart.
   * @param trip Provider's canonical trip record.
   * @param statusOverride Optional staged status to display instead of persisted state.
   */
  const formatTrip = (trip: TripStructure, statusOverride?: string): Trip => {
    // Normalize the provider timestamps to Date instances for chart calculations.
    const startDate = new Date(trip.start);
    const endDate = new Date(trip.end || trip.start);
    // Express the trip length in hours; zero-length records use a one-hour fallback.
    const duration =
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);

    return {
      id: trip.id,
      companiesList: trip.title,
      user: trip.user,
      mainPassenger: trip.passenger || 'Unknown',
      organizer: trip.organizer || 'Unknown',
      listPassenger: trip.usersList,
      outsideUsers: trip.outsideUsers,
      grupo: trip.grupo,
      start_date: startDate,
      end_date: endDate,
      duration: duration || 1,
      carPlate: trip.carPlate,
      originalStart: startDate,
      originalEnd: endDate,
      takecar: trip.takecar,
      leavecar: trip.leavecar,
      isClipped: trip.takecar === 'Y' || trip.leavecar === 'Y',
      isConfirmed: trip.status != 'Pendente',
      statusToSet: statusOverride,
      status: trip.status // Keep track of the actual status
    } as TripRequest;
  };

  /**
   * Returns a valid start/end interval, deriving the end from duration if needed.
   * @param item Formatted trip whose interval is being normalized.
   */
  const getDateRange = (
    item: Pick<Trip, 'start_date' | 'end_date' | 'duration'>
  ): { start: Date; end: Date } => {
    // Normalize the start value in case the caller supplied a date-like value.
    const start = new Date(item.start_date);
    // Prefer a real end timestamp; otherwise derive it from the trip duration.
    const endDate = new Date(item.end_date);
    const end =
      endDate > start
        ? endDate
        : new Date(start.getTime() + (item.duration || 0) * 60 * 60 * 1000);
    return { start, end };
  };

  /**
   * Tests whether two half-open date intervals overlap.
   * @param start1 Start of the first interval.
   * @param end1 End of the first interval.
   * @param start2 Start of the second interval.
   * @param end2 End of the second interval.
   */
  const isOverlapping = (
    start1: Date,
    end1: Date,
    start2: Date,
    end2: Date
  ): boolean => {
    // Strict comparisons make adjacent intervals non-overlapping.
    return start1 < end2 && end1 > start2;
  };

  /**
   * Counts trips in the overlap-connected booking containing the candidate interval.
   * A full-day trip therefore links otherwise separate morning and afternoon trips.
   * @param trips Existing trips assigned to the same vehicle.
   * @param windowStart Inclusive start of the candidate trip.
   * @param windowEnd Exclusive end of the candidate trip.
   */
  const calculateConnectedTripCount = (
    trips: Trip[],
    windowStart: Date,
    windowEnd: Date
  ): number => {
    // Seed the interval group with the candidate; only existing trips count as occupied.
    const intervals: { start: Date; end: Date; isCandidate: boolean }[] = [
      { start: windowStart, end: windowEnd, isCandidate: true }
    ];

    for (const trip of trips) {
      const { start, end } = getDateRange(trip);
      if (start < end) intervals.push({ start, end, isCandidate: false });
    }

    // Sorting by start makes each connected interval group a single forward sweep.
    intervals.sort(
      (a, b) =>
        a.start.getTime() - b.start.getTime() ||
        a.end.getTime() - b.end.getTime()
    );

    let componentEnd: Date | null = null;
    let componentTripCount = 0;
    let componentHasCandidate = false;

    for (const interval of intervals) {
      // A gap starts a separate booking group; return once the candidate group is complete.
      if (!componentEnd || interval.start >= componentEnd) {
        if (componentHasCandidate) return componentTripCount;

        componentEnd = interval.end;
        componentTripCount = interval.isCandidate ? 0 : 1;
        componentHasCandidate = interval.isCandidate;
        continue;
      }

      // Extend the group so later trips can connect through a long-running booking.
      if (interval.end > componentEnd) componentEnd = interval.end;

      if (interval.isCandidate) {
        componentHasCandidate = true;
      } else {
        componentTripCount++;
      }
    }

    return componentHasCandidate ? componentTripCount : 0;
  };

  /**
   * Checks trip capacity and maintenance conflicts for one vehicle/request pair.
   * @param carPlate Vehicle registration being evaluated.
   * @param request Request that would be assigned to the vehicle.
   * @param carCapacity Maximum number of simultaneous trips supported.
   * @param carEventsOverlap Maintenance and observation events in the request window.
   */
  const checkAvailability = (
    carPlate: string,
    request: Trip,
    carCapacity: number,
    carEventsOverlap: Array<{
      carPlate: string;
      startDate: Date;
      endDate?: Date;
      duration?: number;
      stateId: number;
    }> = []
  ): {
    available: boolean;
    maxConcurrentTrips: number;
    remainingSlots: number;
    hasEventConflict: boolean;
    hasObservationConflict: boolean;
  } => {
    // Reuse the request interval for both trip-capacity and event checks.
    const { start: requestStart, end: requestEnd } = getDateRange(request);
    // Exclude persisted trips with staged decisions before adding staged approvals.
    const pendingRequestIds = new Set(
      pendingManagments.map(management => management.requestId)
    );

    // Build the existing allocation list without the candidate request.
    const existingTripsForCar = [
      ...allTripsForDisplay.filter(
        trip =>
          trip.status === 'Aprovado' &&
          trip.carPlate === carPlate &&
          Number(trip.id) !== Number(request.id) &&
          !pendingRequestIds.has(Number(trip.id))
      ),
      ...pendingManagments
        .filter(
          management =>
            management.typeOfAlocation === 'Aprovado' &&
            management.carPlate === carPlate &&
            management.requestId !== Number(request.id)
        )
        .map(management =>
          allTripsForDisplay.find(
            trip => Number(trip.id) === management.requestId
          )
        )
        .filter((trip): trip is Trip => Boolean(trip))
    ];

    // Count existing trips connected directly or indirectly to the candidate.
    const maxConcurrentTrips = calculateConnectedTripCount(
      existingTripsForCar,
      requestStart,
      requestEnd
    );
    const projectedTripCount = maxConcurrentTrips + 1;

    // Interventions block allocation; observations are advisory only.
    let hasEventConflict = false;
    let hasObservationConflict = false;

    for (const event of carEventsOverlap) {
      if (event.carPlate !== carPlate) continue;
      // Normalize event timestamps and treat events without an end as ongoing.
      const eventStart = new Date(event.startDate);
      const eventEnd = event.endDate ? new Date(event.endDate) : undefined;
      // An ongoing event conflicts if it began before the request ends.
      const overlapsRequest = eventEnd
        ? isOverlapping(requestStart, requestEnd, eventStart, eventEnd)
        : eventStart < requestEnd;

      if (!overlapsRequest) continue;

      if (event.stateId === 2) {
        hasObservationConflict = true;
      } else {
        hasEventConflict = true;
      }
    }

    return {
      available: projectedTripCount <= carCapacity && !hasEventConflict,
      maxConcurrentTrips,
      remainingSlots: carCapacity - maxConcurrentTrips,
      hasEventConflict,
      hasObservationConflict
    };
  };
  /**
   * Formats provider trips and overlays local staged decisions for the chart.
   * The provider data is not mutated; updated trip copies are stored in the map.
   */
  const allTripsForDisplay = useMemo(() => {
    // No provider trips means there is nothing to format or overlay.
    if (!trips) return [];

    // Convert each provider record once, then index records by stable string ID.
    const formattedTrips = trips.map(trip => formatTrip(trip));
    const tripMap = new Map<string, Trip>();

    // Populate the lookup map for staged updates.
    formattedTrips.forEach(t => tripMap.set(String(t.id), t));

    // Overlay proposed assignments/statuses without mutating provider trip objects.
    pendingManagments.forEach(managment => {
      // Find the display copy corresponding to this staged decision.
      const trip = tripMap.get(String(managment.requestId));
      if (trip) {
        // trip.carPlate = managment.carPlate;
        tripMap.set(String(managment.requestId), {
          ...trip,
          carPlate: managment.carPlate,
          statusToSet: managment.typeOfAlocation
        });
      }
    });

    // Return the final set of trips consumed by the Gantt chart and availability checks.
    return Array.from(tripMap.values());
  }, [trips, pendingManagments]);

  // Subsets used to distinguish approved trips, unprocessed requests, and refusals.
  const confirmedTrips = useMemo(
    () => allTripsForDisplay.filter(t => t.status === 'Aprovado'),
    [allTripsForDisplay]
  );
  const pendingRequests = useMemo(
    () => allTripsForDisplay.filter(t => t.status === 'Pendente'),
    [allTripsForDisplay]
  );
  const refusedTrips = useMemo(
    () => allTripsForDisplay.filter(t => t.status === 'Recusado'),
    [allTripsForDisplay]
  );

  /**
   * Resolves the modal selection from the current trip subsets.
   * Confirmed trips are augmented with a passenger-capacity fallback from their vehicle.
   */
  const selectedRequest = useMemo(() => {
    // No selected ID means the details modal has no request to resolve.
    if (!selectedRequestId) return null;

    // Check pending requests first
    // Pending requests are the main allocation workflow and take precedence.
    const pendingReq = pendingRequests.find(
      r => r.id === Number(selectedRequestId)
    );

    if (pendingReq) return pendingReq;

    // Check confirmed trips
    // Confirmed trips may still be opened to change their existing status/allocation.
    const confirmedTrip = confirmedTrips.find(
      t => t.id === Number(selectedRequestId)
    );

    if (confirmedTrip) {
      return {
        id: confirmedTrip.id,
        companiesList: confirmedTrip.companiesList,
        mainPassenger: confirmedTrip.mainPassenger,
        organizer: confirmedTrip.organizer,
        listPassenger: confirmedTrip.listPassenger,
        grupo: confirmedTrip.grupo,
        start_date: confirmedTrip.start_date,
        end_date: confirmedTrip.end_date,
        duration: confirmedTrip.duration,
        takecar: confirmedTrip.takecar,
        leavecar: confirmedTrip.leavecar,
        isConfirmed: confirmedTrip.isConfirmed,
        // Preserve known passenger capacity or derive it from the assigned vehicle.
        maxPassengers:
          (confirmedTrip as TripRequest).maxPassengers ||
          carsTrips.find(v => v.carPlate === (confirmedTrip as Trip).carPlate)
            ?.capacity ||
          1
      } as TripRequest;
    }

    // Check refused requests first
    // Refused requests can be reopened if the user selects them from the chart.
    const refusedReq = refusedTrips.find(
      r => r.id === Number(selectedRequestId)
    );
    if (refusedReq) return refusedReq;

    return null;
  }, [
    selectedRequestId,
    pendingRequests,
    confirmedTrips,
    refusedTrips,
    carsTrips
  ]);

  // IDs passed to the chart so it can visually distinguish staged requests.
  const pendingTripIdsForGantt = useMemo(() => {
    return pendingManagments.map(pa => pa.requestId);
  }, [pendingManagments]);

  // Stores the trip IDs currently visible in the Gantt chart.
  const handleVisibleTripsChange = useCallback((tripIds: number[]) => {
    setVisibleTripIds(tripIds);
  }, []);

  // Stores the trip IDs visible in the complete Monday-to-Friday chart week.
  const handleVisibleWeeklyTripsChange = useCallback((tripIds: number[]) => {
    setVisibleWeeklyTripIds(tripIds);
  }, []);

  // Sets provide constant-time membership checks when filtering staged changes.
  const visibleTripIdsSet = useMemo(
    () => new Set(visibleTripIds),
    [visibleTripIds]
  );
  const visibleWeeklyTripIdsSet = useMemo(
    () => new Set(visibleWeeklyTripIds),
    [visibleWeeklyTripIds]
  );
  // Counts unprocessed requests in the full visible work week for the summary.
  const visiblePendingRequestsCount = useMemo(
    () =>
      pendingRequests.filter(request =>
        visibleWeeklyTripIdsSet.has(Number(request.id))
      ).length,
    [pendingRequests, visibleWeeklyTripIdsSet]
  );
  // Limits confirmation and cancellation actions to currently visible chart items.
  const visiblePendingManagments = useMemo(
    () =>
      pendingManagments.filter(managment =>
        visibleTripIdsSet.has(managment.requestId)
      ),
    [pendingManagments, visibleTripIdsSet]
  );

  /**
   * Evaluates and sorts vehicles for the selected request.
   * Valid cars appear first; observation warnings and interventions remain visible.
   */
  const availableVehiclesForRequest = useMemo(() => {
    // A missing selection means there is no request-specific availability to show.
    if (!selectedRequest) return [];

    // Attach capacity and maintenance results to each vehicle for rendering.
    const vehicles = carsTrips.map(vehicle => {
      // Result fields drive both sorting and visual feedback in the vehicle list.
      const {
        available,
        maxConcurrentTrips,
        remainingSlots,
        hasEventConflict,
        hasObservationConflict
      } = checkAvailability(
        vehicle.carPlate,
        selectedRequest,
        vehicle.capacity,
        CarEventsOverlap
      );

      return {
        ...vehicle,
        isValid: available,
        maxConcurrentTrips,
        remainingSlots,
        hasEventConflict,
        hasObservationConflict,
        // hasGroupConflict,
        error: !available
          ? hasEventConflict
            ? `O carro tem uma intervenção agendada neste período.`
            : `O carro já está cheio.`
          : null
      };
    });

    // Sort valid choices before invalid ones, then order advisory/conflict states.
    return [...vehicles].sort((a, b) => {
      if (a.isValid !== b.isValid) {
        return a.isValid ? -1 : 1;
      }

      if (a.hasObservationConflict !== b.hasObservationConflict) {
        return a.hasObservationConflict ? 1 : -1;
      }

      if (a.hasEventConflict !== b.hasEventConflict) {
        return a.hasEventConflict ? 1 : -1;
      }

      return 0;
    });
  }, [
    selectedRequest,
    carsTrips,
    allTripsForDisplay,
    pendingManagments,
    CarEventsOverlap
  ]);

  /**
   * Selects a request and restores its locally staged vehicle, if present.
   * @param tripId ID of the chart trip to open.
   */
  const handleAssignCar = useCallback(
    (tripId: number) => {
      // Reset errors from the previous modal selection.
      setSelectedRequestId(Number(tripId));
      setValidationErrors([]);

      // Look up any staged assignment so reopening preserves the user's choice.
      const existingManagment = pendingManagments.find(
        pa => pa.requestId === tripId
      );

      if (existingManagment?.carID && existingManagment.carPlate) {
        setSelectedVehicle({
          id: existingManagment.carID,
          carPlate: existingManagment.carPlate
        });
      } else {
        setSelectedVehicle(null);
      }
    },
    [pendingManagments]
  );

  // Load maintenance overlaps for the selected request and synchronize modal visibility.
  useEffect(() => {
    if (selectedRequestId && selectedRequest) {
      // Only open the modal once a valid request is loaded
      fetchCarsMaintenanceOverlap(
        selectedRequest.start_date,
        selectedRequest.end_date
      );
      setShowModal(true);
    } else if (!selectedRequestId) {
      // Close modal if ID is cleared
      cleanData();
    }
  }, [selectedRequestId, selectedRequest]);

  /**
   * Validates a chosen vehicle before storing it as the request's current selection.
   * @param carPlate Registration of the vehicle the user clicked.
   */
  const handleVehicleSelect = (carPlate: string | null) => {
    if (selectedVehicle?.carPlate === carPlate) {
      const isAlreadyAssigned = pendingManagments.some(
        management =>
          management.carPlate === carPlate &&
          management.requestId === selectedRequest?.id
      );
      if (isAlreadyAssigned) return;

      const previouslyAssignedVehicle = pendingManagments.find(
        management =>
          management.requestId === selectedRequest?.id &&
          management.carID !== null &&
          management.carPlate !== null
      );
      setSelectedVehicle(
        previouslyAssignedVehicle
          ? {
              id: previouslyAssignedVehicle.carID,
              carPlate: previouslyAssignedVehicle.carPlate
            }
          : null
      );
      setValidationErrors([]);
      return;
    }

    // Resolve the vehicle record needed for its capacity and persisted ID.
    const vehicle = carsTrips.find(v => v.carPlate === carPlate);
    if (vehicle && selectedRequest) {
      // Recheck at selection time so stale list state cannot approve an invalid car.
      const availability = checkAvailability(
        vehicle.carPlate,
        selectedRequest,
        vehicle.capacity,
        CarEventsOverlap
      );

      if (!availability.available) {
        setSelectedVehicle(null);
        setValidationErrors([
          availability.hasEventConflict
            ? `Carro ${vehicle.carPlate} tem uma intervenção agendada neste período.`
            : `Carro ${vehicle.carPlate} já está cheio.`
        ]);
        return;
      }

      setSelectedVehicle({ id: vehicle.id, carPlate: vehicle.carPlate });
      setValidationErrors([]);
    }
  };

  /**
   * Stages an approval/refusal for the selected request without submitting it.
   * @param type Status to stage; approvals require a selected, valid vehicle.
   */
  const handleAddToPending = (type: string) => {
    if (
      !selectedRequest ||
      (type === 'Aprovado' && (!selectedVehicle || validationErrors.length > 0))
    )
      return;

    // Remove the previous proposal for this request before replacing it.
    const filteredManagments = pendingManagments.filter(
      pa => pa.requestId !== selectedRequest.id
    );

    //on it - dosen't have type of affectation
    setPendingManagments([
      ...filteredManagments,
      {
        requestId: selectedRequest.id as number,
        carID:
          type === 'Recusado'
            ? null
            : !selectedVehicle
              ? null
              : selectedVehicle.id,
        carPlate:
          type === 'Recusado'
            ? null
            : !selectedVehicle
              ? null
              : selectedVehicle.carPlate,
        grupo: selectedRequest.grupo,
        typeOfAlocation: type
      }
    ]);
    cleanData();
  };

  /**
   * Removes the selected request's staged change and closes its details modal.
   */
  const handleRemoveFromPending = () => {
    if (!selectedRequest) return;

    setPendingManagments(prev =>
      prev.filter(pa => pa.requestId !== selectedRequest.id)
    );

    cleanData();
  };

  /**
   * Submits only staged changes represented in the current chart viewport.
   * Successful records are removed only if their values have not changed in flight.
   */
  const handleConfirmVisibleChanges = async () => {
    // Snapshot the visible changes so later user edits are not included mid-request.
    const changesToConfirm = visiblePendingManagments;
    if (changesToConfirm.length === 0) return;

    setIsConfirming(true);

    try {
      // Translate local management records to the provider's bulk-update payload.
      const updates = changesToConfirm.map(management => ({
        tripId: management.requestId,
        status: management.typeOfAlocation,
        carId: management.carID
      }));
      // Submit all selected updates together and preserve local changes on failure.
      const result = await bulkUpdateTrips(updates);

      if (result.success) {
        // Compare against the snapshot so edits made while saving are retained.
        const confirmedChanges = new Map(
          changesToConfirm.map(management => [management.requestId, management])
        );
        setPendingManagments(current =>
          current.filter(management => {
            // A matching unchanged proposal was persisted and can be discarded.
            const confirmed = confirmedChanges.get(management.requestId);
            return (
              !confirmed ||
              confirmed.carID !== management.carID ||
              confirmed.carPlate !== management.carPlate ||
              confirmed.typeOfAlocation !== management.typeOfAlocation ||
              confirmed.grupo !== management.grupo
            );
          })
        );
      } else {
        alert('Failed to confirm trips.');
      }
    } catch (error) {
      // Preserve a useful error message for both standard and unknown thrown values.
      const message =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred.';
      alert(`Failed to confirm trips: ${message}`);
    } finally {
      setIsConfirming(false);
    }
  };

  /**
   * Discards only staged changes currently visible in the chart.
   */
  const handleCancelVisibleChanges = () => {
    // Build the IDs to remove, leaving off-screen proposals untouched.
    const visibleRequestIds = new Set(
      visiblePendingManagments.map(management => management.requestId)
    );
    setPendingManagments(current =>
      current.filter(management => !visibleRequestIds.has(management.requestId))
    );
  };

  /**
   * Removes the staged proposal for a trip cancelled directly in the Gantt chart.
   * @param tripId Chart trip ID, which may be string- or number-valued.
   */
  const handleCancelTrip = useCallback((tripId: string | number) => {
    // Normalize IDs because chart records may use either representation.
    const numericTripId = Number(tripId);
    setPendingManagments(prev =>
      prev.filter(pa => pa.requestId !== numericTripId)
    );
  }, []); // Stable reference

  /**
   * Converts decimal hours into a compact hours/minutes label.
   * @param decimalHours Duration value to display.
   */
  function formatDecimalHours(decimalHours: number): string {
    // Split the duration into whole hours and the rounded remainder in minutes.
    const hours = Math.floor(decimalHours);
    // Round the fractional hour to the nearest minute.
    const minutes = Math.round((decimalHours - hours) * 60);
    // Pad the minute value so labels remain consistent, such as "2h05".
    const paddedMinutes = minutes.toString().padStart(2, '0');
    return `${hours}h${paddedMinutes}`;
  }

  // Render the request summary, Gantt chart, and selection modal for this page.
  return (
    <div>
      <div className="container-confirmation d-flex flex-column flex-md-row p-2">
        {/* Summarize the current week's pending requests and staged decisions. */}
        <div className="title-confirmation mb-1">
          <h3 className="mb-1">Gestão de pedidos</h3>
          <div className="confirmation-summary">
            <span>Esta semana existem: </span>
            <span> - {visiblePendingRequestsCount} pedididos por tratar</span>
            <span> - {visiblePendingManagments.length} pedidos tratados</span>
          </div>
        </div>
        {/* Save or discard only staged decisions represented in the visible chart. */}
        <div className="buttons-confirmation d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
          <Button
            onClick={handleConfirmVisibleChanges}
            variant="success"
            disabled={isConfirming || visiblePendingManagments.length === 0}
            className="confirmation-button px-4 py-2 w-100"
          >
            {isConfirming ? (
              <Spinner
                animation="border"
                role="status"
                variant="light"
                size="sm"
              />
            ) : (
              `Confirmar (${visiblePendingManagments.length})`
            )}
          </Button>
          <Button
            onClick={handleCancelVisibleChanges}
            variant="outline-danger"
            disabled={isConfirming || visiblePendingManagments.length === 0}
            className="confirmation-button px-4 py-2 w-100"
          >
            Anular
          </Button>
        </div>
      </div>
      {/* The Gantt chart reports selection, visibility, and cancellation events upward. */}
      <div className="container-ganttChart">
        <BasicGanttChart
          trips={allTripsForDisplay}
          pendingTripIds={pendingTripIdsForGantt}
          initialViewMode={'week'}
          onCancelTrip={handleCancelTrip}
          onVisibleTripsChange={handleVisibleTripsChange}
          onVisibleWeeklyTripsChange={handleVisibleWeeklyTripsChange}
          onAssignCar={handleAssignCar}
        />
      </div>
      {showModal && selectedRequest && (
        /* The modal is only mounted while a request is selected and marked open. */
        <Modal
          show={showModal}
          contentClassName="border-translucent modal-alocation-container"
          onHide={() => {
            cleanData();
          }}
          centered
        >
          <Modal.Header className="alocation-header-container px-card pb-0 border-0">
            <div className="w-100 d-flex justify-content-between">
              <h3 className="alocation-header-title"> Detalhes </h3>
              <Button
                className="alocation-header-quit p-1 fs-10 text-body"
                onClick={() => cleanData()}
              >
                X
              </Button>
            </div>
            <div className="alocation-header-info-container">
              <div className="alocation-header-info-items first-item spacing">
                <p className="m-0">
                  <strong>
                    {selectedRequest?.companiesList.split(',').length > 1
                      ? 'Destinações:'
                      : 'Destinação:'}
                  </strong>{' '}
                  {selectedRequest?.companiesList}
                </p>
              </div>
              <p className="alocation-header-info-items">
                <strong>Pedido de:</strong> {selectedRequest?.mainPassenger}
              </p>
              {/* <p className="alocation-header-info-items">
                <strong>Organizador/a:</strong> {selectedRequest?.organizer}
              </p> */}
              {selectedRequest.takecar == 'Y' && (
                <p className="alocation-header-info-items">
                  <strong>Quer/precisa de levar o carro no dia anterior</strong>
                </p>
              )}
              {selectedRequest.leavecar == 'Y' && (
                <p className="alocation-header-info-items">
                  <strong>
                    Quer/precisa de deixar o carro no dia seguinte
                  </strong>
                </p>
              )}
              <p className="alocation-header-info-items">
                <strong>Grupo: </strong>
                {selectedRequest?.listPassenger.split(',').length}{' '}
                {selectedRequest?.listPassenger.split(',').length > 1
                  ? 'pessoas'
                  : 'pessoa'}
                {' - '}
                {selectedRequest?.listPassenger}
              </p>
              {selectedRequest.outsideUsers > 0 && (
                <p className="alocation-header-info-items">
                  <strong>Acompanhantes externos: </strong>
                  {selectedRequest?.outsideUsers}
                </p>
              )}
              {/* Show unique vehicle assignments already associated with this trip group. */}
              {(() => {
                // Collect distinct, non-empty plates assigned to any trip in the group.
                const carPlates = [
                  ...new Set(
                    allTripsForDisplay
                      .filter(
                        item =>
                          item.grupo == selectedRequest.grupo &&
                          item.carPlate != null
                      )
                      .map(item => item.carPlate as string)
                  )
                ];

                if (carPlates.length === 0) return null;

                // Choose a singular or plural label to match the number of vehicles.
                const label =
                  carPlates.length === 1
                    ? 'Carro atribuído:'
                    : 'Carros atribuídos:';
                // Join the assignments into a readable comma-separated display value.
                const value = carPlates.join(', ');

                return (
                  <p className="alocation-header-info-items">
                    <strong>{label}</strong> {value}
                  </p>
                );
              })()}
              <p className="alocation-header-info-items">
                <strong>Duração: </strong>
                {formatDecimalHours(selectedRequest?.duration)}
              </p>
              <div className="alocation-header-info-items spacing last-item">
                <p className="alocation-header-info-items">
                  <strong>Início:</strong>{' '}
                  {selectedRequest && (
                    <>{formatDateTime(selectedRequest.start_date)}</>
                  )}
                </p>
                <p className="alocation-header-info-items">
                  <strong>Fim:</strong>{' '}
                  {selectedRequest && (
                    <>{formatDateTime(selectedRequest.end_date)}</>
                  )}
                </p>
              </div>
            </div>
          </Modal.Header>
          {!selectedRequest.isConfirmed && (
            <Modal.Body className="alocation-body-container p-card py-0">
              <h3 className="alocation-body-title"> Selecionar veículo </h3>
              <div className="alocation-body-carScroll">
                {/* Each row includes capacity, availability, and advisory status for a vehicle. */}
                {availableVehiclesForRequest.map(
                  ({
                    carPlate,
                    capacity,
                    isValid,
                    error,
                    hasObservationConflict,
                    maxConcurrentTrips
                  }) => {
                    // The callback receives vehicle identity, capacity, and precomputed validation.
                    // Highlight the vehicle already staged for this request.
                    const isAssigned = pendingManagments.some(
                      pa =>
                        pa.carPlate === carPlate &&
                        pa.requestId === selectedRequest?.id
                    );
                    // Highlight a vehicle selected in the current modal session.
                    const isNewlySelected =
                      selectedVehicle?.carPlate === carPlate;
                    return (
                      <div
                        key={carPlate}
                        className={`
                      alocation-body-content 
                      ${hasObservationConflict ? 'alocation-body-content-observation-conflict' : ''}
                      ${
                        isAssigned
                          ? 'alocation-body-content-preselected'
                          : isNewlySelected
                            ? 'alocation-body-content-active'
                            : isValid
                              ? 'alocation-body-content-valid'
                              : 'alocation-body-content-invalid'
                      }`}
                        onClick={() => {
                          if (isValid) {
                            handleVehicleSelect(carPlate);
                          }
                        }}
                      >
                        <div className="alocation-body-items">
                          <div className="d-flex justify-content-between w-100">
                            <div>
                              <strong>{carPlate}</strong>
                            </div>
                            <div>
                              <span className="alocation-body-item-capacity">
                                Lugares: {maxConcurrentTrips}/{capacity}
                              </span>
                            </div>
                          </div>
                          {/* <div>
                          {isValid ? (
                            <span className="alocation-body-item-valid">✓</span>
                          ) : (
                            <span className="alocation-body-item-invalid">
                              ✗
                            </span>
                          )}
                        </div> */}
                        </div>
                        {!isValid && error && (
                          <div className="alocation-body-error">
                            <div>• {error}</div>
                          </div>
                        )}
                        {hasObservationConflict && (
                          <div className="alocation-body-observation-conflict">
                            <div>• O carro tem uma observação</div>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
              {validationErrors.length > 0 && (
                /* Explain why the current vehicle selection cannot be approved. */
                <div
                  style={{
                    padding: '15px',
                    backgroundColor: '#ffebee',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}
                >
                  <strong style={{ color: '#c62828' }}>
                    Não pode atribuir esse carro:
                  </strong>
                  {/* Render every validation reason with a stable position key. */}
                  {validationErrors.map((error, idx) => (
                    <div
                      key={idx}
                      style={{
                        marginTop: '5px',
                        fontSize: '14px',
                        color: '#c62828'
                      }}
                    >
                      • {error}
                    </div>
                  ))}
                </div>
              )}
            </Modal.Body>
          )}
          <Modal.Footer
            className={`alocation-footer-container d-flex border-0 pt-0 ${selectedRequest.isConfirmed ? 'mt-3' : ''}`}
          >
            {!selectedRequest.isConfirmed &&
              pendingManagments.some(
                pa => pa.requestId === selectedRequest.id
              ) && (
                <Button
                  onClick={handleRemoveFromPending}
                  className="alocation-footer-button w-100"
                >
                  Retirar de pré-visualização
                </Button>
              )}
            {!selectedRequest.isConfirmed ? (
              <div className="alocation-footer-confirmation-group w-100">
                <Button
                  onClick={() => {
                    // Stage a refusal; no vehicle is required for this decision.
                    const state = 'Recusado';
                    handleAddToPending(state);
                  }}
                  className={`
                    w-50
                    alocation-footer-button 
                    alocation-footer-confirmation-button
                    alocation-footer-confirmation-button-available-recusar
                  `}
                >
                  Recusar
                </Button>
                <Button
                  onClick={() => {
                    // Stage approval only after the selected vehicle passes validation.
                    const state = 'Aprovado';
                    handleAddToPending(state);
                  }}
                  disabled={
                    !selectedVehicle ||
                    validationErrors.length > 0 ||
                    !availableVehiclesForRequest.some(
                      vehicle =>
                        vehicle.carPlate === selectedVehicle.carPlate &&
                        vehicle.isValid
                    )
                  }
                  className={`
                    w-50
                    alocation-footer-button 
                    alocation-footer-confirmation-button
                    ${
                      validationErrors.length === 0 && selectedVehicle
                        ? 'alocation-footer-confirmation-button-available'
                        : ''
                    }
                  `}
                >
                  Approvar
                </Button>
              </div>
            ) : pendingManagments.some(
                pa => pa.requestId === selectedRequest.id
              ) ? (
              <Button
                onClick={handleRemoveFromPending}
                className={`
                    w-100
                    alocation-footer-button 
                    alocation-footer-confirmation-button
                  `}
              >
                Anular mudança
              </Button>
            ) : (
              <Button
                onClick={() => {
                  // Return an already-confirmed trip to the pending state.
                  const state = 'Pendente';
                  handleAddToPending(state);
                }}
                className={`
                    w-100
                    alocation-footer-button 
                    alocation-footer-confirmation-button
                  `}
              >
                Retirar da tabela
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default index;
